import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import type { CollabRequest } from '../types';

const ROLE_LABEL: Record<string, string> = {
  VOCALIST: 'Need Vocalist',
  PRODUCER: 'Need Producer',
  MIXING: 'Need Mixing',
  SONGWRITER: 'Need Songwriter',
  OTHER: 'Other',
};

export function CollabsPage() {
  const [collabs, setCollabs] = useState<CollabRequest[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [role, setRole] = useState<CollabRequest['role']>('VOCALIST');
  const [description, setDescription] = useState('');
  const [interested, setInterested] = useState<string[]>([]);
  const [interestMsg, setInterestMsg] = useState('');
  const navigate = useNavigate();

  const {currentUser} = useApp();
  const [busy,setBusy] = useState(false);
  const load = async () => { try { const d=await api.collabs(); setCollabs(d.collabs); setInterested(d.collabs.filter(c=>c.interested).map(c=>c.id)); } catch(e:any) {setInterestMsg(e.message);} };
  useEffect(()=>{load();},[]);
  const close = async (id:string) => { try {await api.closeCollab(id); await load();}catch(e:any){setInterestMsg(e.message);} };
  const create = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.createCollab({title:title.trim(),role,description:description.trim()});
      await load(); setTitle(''); setDescription(''); setRole('VOCALIST'); setShowForm(false);
    } catch(e:any) {setInterestMsg(e.message);} finally {setBusy(false);}
  };
  const sendInterest = async (id:string,title:string) => {
    if(interested.includes(id))return;
    try {await api.collabInterest(id);setInterested([...interested,id]);setInterestMsg(`Interest sent for “${title}”. The creator has an in-app notification.`);}
    catch(e:any){setInterestMsg(e.message);}
  };

  return (
    <div className="grid">
      <div className="card">
        <div className="flex-between wrap gap-8">
          <div>
            <div className="section-title" style={{ margin: 0 }}>Collabs</div>
            <div className="muted" style={{ fontSize: 13 }}>
              Find or manage music collaborations. Post a request, send interest,
              and message other artists directly in the Messages page.
              <button className="btn btn-outline btn-sm" style={{ marginLeft: 8 }} onClick={() => navigate('/app/messages')}>
                Go to Messages →
              </button>
            </div>
          </div>
          <button className="btn btn-gold btn-sm" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Close' : '+ Create Collaboration Post'}
          </button>
        </div>

        {showForm && (
          <div className="mt-16" style={{ marginTop: 16 }}>
            <div className="form-group">
              <label className="label">Title <span className="req">*</span></label>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Need a clean vocalist for an R&B hook" />
            </div>
            <div className="form-group">
              <label className="label">Role needed</label>
              <select className="select" value={role} onChange={(e) => setRole(e.target.value as CollabRequest['role'])}>
                {Object.keys(ROLE_LABEL).map((r) => (
                  <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="label">Details</label>
              <textarea className="textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe what you need. Remember: collaborators write their own lyrics — no AI lyrics allowed." />
            </div>
            <button className="btn btn-primary btn-sm" onClick={create} disabled={busy || !title.trim()}>Post request</button>
          </div>
        )}
      </div>

      {collabs.length===0 && <div className="empty">No collaboration requests yet. Post the first one.</div>}
      <div className="grid cols-2">
        {collabs.map((c) => (
          <div key={c.id} className="card" style={{ background: 'var(--iam-gray-2)' }}>
            <div className="flex-between gap-8 mb-8">
              <strong>{c.title}</strong>
              <span className="chip chip-requested">{ROLE_LABEL[c.role]}</span>
            </div>
            <p className="muted" style={{ fontSize: 13.5 }}>{c.description}</p>
            <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              by {c.authorUsername} · {c.status}
            </div>
            <div className="flex gap-8 mt-16" style={{ marginTop: 12 }}>
              <button className="btn btn-outline btn-sm" onClick={() => sendInterest(c.id, c.title)} disabled={interested.includes(c.id) || c.authorUsername===currentUser?.username}>
                {interested.includes(c.id) ? '✓ Interest Sent' : 'Send interest'}
              </button>
              {c.authorUsername !== currentUser?.username && (
                <button className="btn btn-outline btn-sm" onClick={() => navigate(`/app/messages?to=${encodeURIComponent(c.authorUsername)}`)}>
                  Message
                </button>
              )}
              {c.authorUsername === currentUser?.username && (
                <button className="btn btn-outline btn-sm" onClick={() => close(c.id)}>
                  Close
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {interestMsg && <div className="chip chip-approved" style={{ marginTop: 8 }}>{interestMsg}</div>}
    </div>
  );
}
